"use client"

import { Result } from "@praha/byethrow"
import { useCallback, useEffect, useRef, useState, useTransition } from "react"
import { Spinner } from "./spinner"
import { useServerState } from "@mylaunch/components/hooks"

export type PaginatedList<T> = {
  items: T[]
  nextPage?: number
}

interface UpdatableInfiniteListProps<T extends { id: string | number }> extends InfiniteListProps<T>{
  setItems: React.Dispatch<React.SetStateAction<T[]>>
  containerRef?: (node: HTMLDivElement | null) => void
}

export function UpdatableInfiniteList<T extends { id: string | number }>({
  items,
  setItems,
  initialNextPage,
  loadMore,
  children,
  className,
  containerRef,
}: UpdatableInfiniteListProps<T>) {
  const [nextPage, setNextPage] = useState(initialNextPage)
  const [isPending, startTransition] = useTransition()
  const sentinelRef = useRef<HTMLDivElement>(null)

  const handleLoadMore = useCallback(() => {
    if (isPending || !nextPage) return
    startTransition(async () => {
      const result = await loadMore(nextPage)
      if (Result.isFailure(result)) return

      const value = Result.unwrap(result)
      setItems((prev) => [...prev, ...value.items])
      setNextPage(value.nextPage)
    })
  }, [isPending, nextPage, loadMore])

  useEffect(() => {
    const el = sentinelRef.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e?.isIntersecting) handleLoadMore()
      },
      { rootMargin: "200px" },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [handleLoadMore])

  return (
    <div ref={containerRef} className={className}>
      {items.map((item) => (
        <div key={item.id}>{children(item)}</div>
      ))}
      {nextPage && (
        <div ref={sentinelRef} className="flex justify-center p-m">
          {isPending && <Spinner className="size-8" />}
        </div>
      )}
    </div>
  )
}

interface InfiniteListProps<T extends { id: string | number }> {
  items: T[]
  initialNextPage?: number
  loadMore: (page: number) => Result.ResultAsync<PaginatedList<T>, string>
  children: (item: T) => React.ReactNode
  className?: string
  containerRef?: (node: HTMLDivElement | null) => void
}

export function InfiniteList<T extends { id: string | number }>(props: InfiniteListProps<T>) {
  const [items, setItems] = useServerState(props.items)

  return <UpdatableInfiniteList {...props} items={items} setItems={setItems} />
}
